import React, { useState, useEffect, useRef } from "react";
import { cn } from "@quake/shared-ui/lib/utils";

export interface VariableTag {
  tag: string;
  label: string;
  description?: string;
}

export interface SimpleLiquidSuggestionProps {
  isVisible: boolean;
  items: VariableTag[];
  onSelect: (tag: VariableTag) => void;
  onClose: () => void;
  query: string;
}

export const SimpleLiquidSuggestion: React.FC<SimpleLiquidSuggestionProps> = ({
  isVisible = true,
  items,
  onSelect,
  onClose,
  query,
  ...props
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  console.log("SimpleLiquidSuggestion rendered", { isVisible, items, onSelect, onClose, query, ...props });
  useEffect(() => {
    console.log("SimpleLiquidSuggestion useEffect", { isVisible, items, onSelect, onClose, query, ...props });
    setSelectedIndex(0);
  }, [items]);

  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % items.length);
        scrollToChild(".suggestion-options", selectedIndex + 1);
        // const options = document.querySelector(".suggestion-options");
        // if (!options) return;
        // const children = options.children;
        // if (children && children.length > 0) {
        //   const selectedElement = children[(selectedIndex + 1) % items.length];
        //   if (selectedElement) {
        //     selectedElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
        //   }
        // }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + items.length) % items.length);
        scrollToChild(".suggestion-options", selectedIndex - 1);
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (items[selectedIndex]) {
          onSelect(items[selectedIndex]);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => document.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, [isVisible, items, selectedIndex, onSelect, onClose]);

  if (!isVisible || items.length === 0) {
    return null;
  }
  return (
    <div
      ref={containerRef}
      className="z-100 min-w-80 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
    >
      <div className="px-3 py-2 bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-gray-600">
        <div className="flex items-center text-xs text-gray-600 dark:text-gray-300">
          <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 font-medium">
            {"{"}
          </span>
          <span className="ml-2">Liquid Tags</span>
          {query && <span className="ml-2 text-gray-500 dark:text-gray-400">• "{query}"</span>}
        </div>
      </div>
      <div className="max-h-60 overflow-y-auto suggestion-options">
        {items.map((item, index) => (
          <button
            key={item.tag}
            type="button"
            className={cn(
              "w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700 border-b border-gray-100 dark:border-gray-600 last:border-b-0 transition-colors",
              index === selectedIndex && "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800",
            )}
            onClick={() => onSelect(item)}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="grid items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <div className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-mono text-sm">
                      {`{{ ${item.tag} }}`}
                    </div>
                    <div className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">{item.label}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    {item.description && (
                      <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{item.description}</div>
                    )}
                  </div>
                </div>
              </div>
              {index === selectedIndex && (
                <div className="flex items-center space-x-1 text-xs text-gray-400 dark:text-gray-500 ml-2">
                  <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600">
                    ↵
                  </kbd>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
      <div className="px-3 py-2 bg-gray-50 dark:bg-slate-700 border-t border-gray-200 dark:border-gray-600">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>Use ↑↓ to navigate, ↵ to select</span>
          <span>ESC to close</span>
        </div>
      </div>
    </div>
  );
};

function scrollToChild(parentSelector: string, childIndex: number) {
  const options = document.querySelector(parentSelector);
  if (!options) return;
  const children = options.children;
  if (children && children.length > 0) {
    const selectedElement = children[childIndex % children.length];
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }
}
